'use client'

import { useState, useRef } from 'react'
import type {
	ExternalVideoComponentFragment,
	ImageFragment,
} from '@/lib/types/datocms/graphql/generated'
import { VideoOverlay } from '../InternalVideoComponent'

export default function ExternalVideoComponent({
	video,
	coverImage,
}: ExternalVideoComponentFragment) {
	const videoRef = useRef<HTMLIFrameElement>(null)
	const [isPlaying, setIsPlaying] = useState(false)

	const handlePlayPause = () => {
		setIsPlaying((prev) => {
			const newState = !prev
			if (videoRef.current) {
				const message = newState ? 'play' : 'pause'
				if (video?.provider === 'youtube') {
					videoRef.current.contentWindow?.postMessage(
						JSON.stringify({ event: 'command', func: message }),
						'*'
					)
				} else if (video?.provider === 'vimeo') {
					videoRef.current.contentWindow?.postMessage(JSON.stringify({ method: message }), '*')
				}
			}
			return newState
		})
	}

	const capitalizeWords = (str: string) => {
		return str.replace(/\b\w/g, (char) => char.toUpperCase())
	}

	if (!video?.url) return null

	if (video.provider === 'youtube') {
		return (
			<div className="video-container relative bg-transparent rounded-xl overflow-hidden">
				<iframe
					ref={videoRef}
					src={`https://www.youtube.com/embed/${video.providerUid}?rel=0&autoplay=${isPlaying ? 1 : 0}&controls=0`}
					className="aspect-video w-full"
					allowFullScreen
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					title={capitalizeWords(video.title)}
				/>
				{!isPlaying ? (
					<VideoOverlay
						isPlaying={isPlaying}
						togglePlayPause={handlePlayPause}
						title={capitalizeWords(video.title)}
						coverImage={coverImage?.[0] as ImageFragment}
					/>
				) : null}
			</div>
		)
	}

	if (video.provider === 'vimeo') {
		return (
			<div className="video-container relative bg-transparent rounded-xl overflow-hidden">
				<iframe
					ref={videoRef}
					src={`https://player.vimeo.com/video/${video.providerUid}?autoplay=${isPlaying ? 1 : 0}`}
					className="aspect-video w-full"
					allowFullScreen
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					title={capitalizeWords(video.title)}
				/>
				<VideoOverlay
					isPlaying={isPlaying}
					togglePlayPause={handlePlayPause}
					title={capitalizeWords(video.title)}
					coverImage={coverImage?.[0] as ImageFragment}
				/>
			</div>
		)
	}

	return (
		<div className="aspect-video w-full rounded-xl flex items-center justify-center bg-white-frosted-shade mt-12">
			<p className="text-center">Only videos from Youtube or Vimeo can be embedded.</p>
		</div>
	)
}
